import type {
    SiteBuilderContentJson,
    SiteBuilderGlobalLayout,
} from "./siteBuilderTypes";
import {
    applyWebFooterLayoutSkeleton,
    normalizeWebFooterVariant,
    WEB_FOOTER_LAYOUT_SKELETON_VERSION,
    WEB_FOOTER_LAYOUT_VARIANTS,
    type WebFooterVariant,
} from "./webFooterLayout";

export const WEB_GLOBAL_LAYOUT_LANGUAGE_DOCUMENTS_KEY = "webLanguageDocuments";
export const WEB_FOOTER_VARIANTS = WEB_FOOTER_LAYOUT_VARIANTS;
export type { WebFooterVariant } from "./webFooterLayout";

type WebLanguageDocuments = Record<string, SiteBuilderContentJson>;

function normalizedFooterVariant(
    value: unknown,
    fallback: string = "columns",
): WebFooterVariant {
    return normalizeWebFooterVariant(value, fallback);
}

function normalizedLanguageCode(languageCode: string | null | undefined): string {
    return languageCode?.trim().toLowerCase() ?? "";
}

function languageDocuments(
    document: SiteBuilderContentJson | null | undefined,
): WebLanguageDocuments {
    const value = document?.[WEB_GLOBAL_LAYOUT_LANGUAGE_DOCUMENTS_KEY];

    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return {};
    }

    return Object.fromEntries(Object.entries(value).flatMap(([code, candidate]) => (
        candidate && typeof candidate === "object" && !Array.isArray(candidate)
            ? [[normalizedLanguageCode(code), candidate as SiteBuilderContentJson]]
            : []
    )));
}

function withoutLanguageDocuments(
    document: SiteBuilderContentJson,
): SiteBuilderContentJson {
    const next = structuredClone(document);
    delete next[WEB_GLOBAL_LAYOUT_LANGUAGE_DOCUMENTS_KEY];

    return next;
}

function footerCanonicalOptions(
    document: SiteBuilderContentJson,
): {
    stackOnMobile: unknown[];
    variants: WebFooterVariant[];
} | null {
    const blocks = Array.isArray(document.blocks) ? document.blocks : [];

    if (!blocks.some((block) => block.type === "footer")) {
        return null;
    }

    return {
        stackOnMobile: blocks.map((block) => (
            block.options?.stackOnMobile ?? true
        )),
        variants: blocks.map((block) => normalizedFooterVariant(
            block.options?.footerVariant,
        )),
    };
}

/**
 * Upgrades the root canvas of a Web footer document without touching its
 * editable children. Legacy Web footers were stored as richContent roots;
 * keeping the upgrade at the explicit footer boundary prevents page/header
 * canvases from being reclassified accidentally.
 */
function normalizeWebFooterDocumentWithVariants(
    document: SiteBuilderContentJson | null | undefined,
    fallbackVariant: string = "columns",
    canonicalVariants?: readonly WebFooterVariant[],
    canonicalStackOnMobile?: readonly unknown[],
): SiteBuilderContentJson | null {
    if (!document) {
        return null;
    }

    const variant = normalizedFooterVariant(fallbackVariant);
    const blocks = Array.isArray(document.blocks) ? document.blocks : [];
    let blocksChanged = false;
    const normalizedBlocks = blocks.map((block, index) => {
        if (block.type !== "richContent" && block.type !== "footer") {
            return block;
        }

        const footerVariant = canonicalVariants?.[index]
            ?? normalizedFooterVariant(
                block.options?.footerVariant,
                variant,
            );
        const stackOnMobile = canonicalStackOnMobile?.[index]
            ?? block.options?.stackOnMobile
            ?? true;

        const normalizedRoot = (
            block.type === "footer"
            && block.options?.footerVariant === footerVariant
            && block.options?.stackOnMobile === stackOnMobile
        )
            ? block
            : {
                ...block,
                type: "footer",
                options: {
                    ...(block.options ?? {}),
                    footerVariant,
                    stackOnMobile,
                },
            };
        const currentSkeleton = (
            normalizedRoot.options?.webFooterLayoutSkeletonVersion
                === WEB_FOOTER_LAYOUT_SKELETON_VERSION
            && normalizedRoot.options?.webFooterLayoutSkeletonVariant
                === footerVariant
        );
        const normalized = currentSkeleton
            ? normalizedRoot
            : applyWebFooterLayoutSkeleton(
                normalizedRoot,
                footerVariant,
            );

        blocksChanged = blocksChanged || normalized !== block;

        return normalized;
    });
    const baseVariants = normalizedBlocks.map((block, index) => (
        block.type === "footer"
            ? normalizedFooterVariant(
                block.options?.footerVariant,
                canonicalVariants?.[index] ?? variant,
            )
            : canonicalVariants?.[index] ?? variant
    ));
    const baseStackOnMobile = normalizedBlocks.map((block, index) => (
        canonicalStackOnMobile?.[index]
        ?? block.options?.stackOnMobile
        ?? true
    ));
    const localizedDocuments = languageDocuments(document);
    let localizedChanged = false;
    const normalizedLocalized = Object.fromEntries(
        Object.entries(localizedDocuments).map(([languageCode, localized]) => {
            const normalized = normalizeWebFooterDocumentWithVariants(
                localized,
                variant,
                baseVariants,
                baseStackOnMobile,
            ) ?? localized;
            localizedChanged = localizedChanged || normalized !== localized;

            return [languageCode, normalized];
        }),
    );

    if (!blocksChanged && !localizedChanged) {
        return document;
    }

    return {
        ...document,
        ...(blocksChanged ? { blocks: normalizedBlocks } : {}),
        ...(localizedChanged
            ? {
                [WEB_GLOBAL_LAYOUT_LANGUAGE_DOCUMENTS_KEY]:
                    normalizedLocalized,
            }
            : {}),
    };
}

export function normalizeWebFooterDocument(
    document: SiteBuilderContentJson | null | undefined,
    fallbackVariant: string = "columns",
): SiteBuilderContentJson | null {
    return normalizeWebFooterDocumentWithVariants(
        document,
        fallbackVariant,
    );
}

export function webGlobalLayoutDocumentForLanguage(
    document: SiteBuilderContentJson | null | undefined,
    languageCode: string,
    defaultLanguageCode: string,
): SiteBuilderContentJson | null {
    if (!document) {
        return null;
    }

    const normalizedLanguage = normalizedLanguageCode(languageCode);
    const normalizedDefault = normalizedLanguageCode(defaultLanguageCode);

    if (
        normalizedLanguage === ""
        || normalizedLanguage === normalizedDefault
    ) {
        return withoutLanguageDocuments(document);
    }

    const localized = languageDocuments(document)[normalizedLanguage];

    return localized
        ? withoutLanguageDocuments(localized)
        : withoutLanguageDocuments(document);
}

export function resolveWebGlobalLayoutLanguage(
    layout: SiteBuilderGlobalLayout | null | undefined,
    languageCode: string,
    defaultLanguageCode: string,
    footerVariant: string = "columns",
): SiteBuilderGlobalLayout | null {
    if (!layout) {
        return null;
    }

    return {
        header: webGlobalLayoutDocumentForLanguage(
            layout.header,
            languageCode,
            defaultLanguageCode,
        ),
        footer: webGlobalLayoutDocumentForLanguage(
            normalizeWebFooterDocument(
                layout.footer,
                footerVariant,
            ),
            languageCode,
            defaultLanguageCode,
        ),
    };
}

export function withWebGlobalLayoutLanguageDocument(
    baseDocument: SiteBuilderContentJson | null | undefined,
    document: SiteBuilderContentJson,
    languageCode: string,
    defaultLanguageCode: string,
): SiteBuilderContentJson {
    const normalizedLanguage = normalizedLanguageCode(languageCode);
    const normalizedDefault = normalizedLanguageCode(defaultLanguageCode);
    const base = baseDocument
        ? structuredClone(baseDocument)
        : structuredClone(document);

    if (
        normalizedLanguage === ""
        || normalizedLanguage === normalizedDefault
    ) {
        const next = withoutLanguageDocuments(document);
        const existingDocuments = languageDocuments(base);

        if (Object.keys(existingDocuments).length > 0) {
            next[WEB_GLOBAL_LAYOUT_LANGUAGE_DOCUMENTS_KEY] = existingDocuments;
        }

        return footerCanonicalOptions(next)
            ? normalizeWebFooterDocumentWithVariants(next) ?? next
            : next;
    }

    base[WEB_GLOBAL_LAYOUT_LANGUAGE_DOCUMENTS_KEY] = {
        ...languageDocuments(base),
        [normalizedLanguage]: withoutLanguageDocuments(document),
    };

    const editedFooterOptions = footerCanonicalOptions(document);

    return editedFooterOptions
        ? normalizeWebFooterDocumentWithVariants(
            base,
            "columns",
            editedFooterOptions.variants,
            editedFooterOptions.stackOnMobile,
        ) ?? base
        : base;
}
